import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { IMAGES } from "@/data/content";
import usePageContent from "@/hooks/usePageContent";

const ease = [0.16, 1, 0.3, 1];

export default function Hero() {
    const t = usePageContent("home");
    return (
        <section
            id="top"
            data-testid="hero-section"
            className="relative min-h-screen w-full overflow-hidden"
        >
            <motion.img
                src={t("hero.image", IMAGES.hero)}
                alt="Intérieur belge contemporain"
                initial={{ scale: 1.08, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 1.8, ease }}
                className="absolute inset-0 w-full h-full object-cover"
                loading="eager"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-[#1E3A5F]/55 via-[#1E3A5F]/25 to-[#1E3A5F]/65" />
            <div className="absolute inset-x-0 top-0 h-40 bg-gradient-to-b from-black/45 to-transparent pointer-events-none" />
            <div className="absolute inset-0 bg-black/10" />

            <div className="relative z-10 min-h-screen flex flex-col justify-end">
                <div className="max-w-[1400px] w-full mx-auto px-6 md:px-12 pt-36 sm:pt-40 md:pt-32 pb-20 md:pb-32">
                        <motion.div
                            initial={{ opacity: 0, y: 24 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 1, delay: 0.4, ease }}
                            className="flex items-center gap-3 mb-8"
                        >
                            <span className="h-px w-12 bg-white/70" />
                            <span className="overline !text-white">
                                {t("hero.overline", "Studio d'aménagement · Etterbeek")}
                            </span>
                        </motion.div>

                        <motion.h1
                            initial={{ opacity: 0, y: 32 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 1.1, delay: 0.55, ease }}
                            data-testid="hero-title"
                            className="font-serif text-white text-4xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-[5.5rem] leading-[1.05] tracking-tight max-w-5xl"
                        >
                            {t("hero.title", "Du mobilier sur mesure")}
                            <br />
                            <span className="italic font-normal text-white/90">
                                {t("hero.title_italic", "pensé pour votre intérieur.")}
                            </span>
                        </motion.h1>

                        <motion.p
                            initial={{ opacity: 0, y: 24 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.9, delay: 0.85, ease }}
                            className="mt-8 text-white/85 text-base md:text-xl max-w-2xl font-light leading-relaxed"
                        >
                            {t(
                                "hero.subtitle",
                                "Cuisine, dressing et aménagement de qualité à Bruxelles. Un savoir-faire artisanal au service d'une architecture intérieure raffinée.",
                            )}
                        </motion.p>

                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.9, delay: 1.05, ease }}
                            className="mt-10 flex flex-col sm:flex-row gap-4"
                        >
                            <a
                                href="#contact"
                                data-testid="hero-cta-quote"
                                className="btn-accent group"
                            >
                                {t("hero.cta_quote", "Demander un devis")}
                                <ArrowRight size={18} strokeWidth={1.5} className="transition-transform group-hover:translate-x-1" />
                            </a>
                            <a
                                href="#realisations"
                                data-testid="hero-cta-gallery"
                                className="btn-light group"
                            >
                                {t("hero.cta_gallery", "Voir nos réalisations")}
                                <ArrowRight size={18} strokeWidth={1.5} className="transition-transform group-hover:translate-x-1" />
                            </a>
                        </motion.div>
                </div>

                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 1, delay: 1.3 }}
                    className="hidden md:flex absolute bottom-8 right-12 items-center gap-3 text-white/70 text-xs uppercase tracking-[0.25em]"
                >
                    <span>Scroll</span>
                    <span className="h-px w-12 bg-white/40" />
                </motion.div>
            </div>
        </section>
    );
}
